<div class="main-content">
	<div class="container-fluid">
		<mat-card class="feature-surface">
			<mat-card-header>
				<div mat-card-avatar class="feature-surface__icon">
					<i class="material-icons" aria-hidden="true">groups</i>
				</div>
				<mat-card-title>A/R Collection Tracker</mat-card-title>
				<mat-card-subtitle>
					Who owes, how late, who called them last, and what they promised
				</mat-card-subtitle>
			</mat-card-header>
			<mat-card-content>

				<div *ngIf="!installed" class="feature-section mb-3">
					<p class="feature-section__title m-0">Note tracking not installed</p>
					<small class="text-muted">{{ installMessage }} Aging below is live either way.</small>
				</div>

				<!-- Filters. Aging buckets are chips (same vocabulary as the flyout's
				     outcome row); the rest are native controls. -->
				<div class="collection-tracker__filters">
					<div class="collection-tracker__buckets" role="group" aria-label="Aging bucket">
						<button type="button" class="feature-chip is-sm"
							*ngFor="let b of buckets"
							[class.is-on]="filters.bucket === b.value"
							[attr.aria-pressed]="filters.bucket === b.value"
							(click)="setBucket(b.value)">
							{{ b.label }}
						</button>
					</div>

					<div class="ff-field ff-field--md">
						<label class="ff-label" for="ct-state">Collection state</label>
						<div class="ff-control">
							<select id="ct-state" [(ngModel)]="filters.followup" (change)="load(true)" [disabled]="!installed">
								<option *ngFor="let f of followupFilters" [value]="f.value">{{ f.label }}</option>
							</select>
						</div>
					</div>

					<div class="ff-field ff-field--sm">
						<label class="ff-label" for="ct-rep">Rep code</label>
						<div class="ff-control">
							<input id="ct-rep" [(ngModel)]="filters.salesperson" (change)="load(true)">
						</div>
					</div>

					<div class="ff-field ff-field--sm">
						<label class="ff-label" for="ct-min">Min balance</label>
						<div class="ff-control is-num">
							<span class="ff-prefix">$</span>
							<input id="ct-min" type="number" step="1" min="0" [(ngModel)]="filters.min_balance" (change)="load(true)">
						</div>
					</div>

					<div class="ff-field ff-field--md">
						<label class="ff-label" for="ct-search">Customer or account #</label>
						<div class="ff-control">
							<input id="ct-search" [(ngModel)]="filters.search" (keyup.enter)="load(true)" placeholder="name or number">
						</div>
					</div>

					<div class="collection-tracker__filter-actions">
						<button mat-stroked-button (click)="resetFilters()">Reset</button>
						<button mat-stroked-button color="accent" (click)="load(true)">Refresh</button>
					</div>
				</div>

				<!-- In-flight refresh: a hairline, not a spinner swap, so the rows
				     and your scroll position stay put. -->
				<div class="collection-tracker__refreshing" *ngIf="refreshing" aria-hidden="true"></div>

				<div *ngIf="loading" class="text-center my-4">
					<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
				</div>

				<div *ngIf="!loading">
					<!-- Tiles -->
					<div class="feature-tile-row" *ngIf="summary">
						<app-kpi-tile
							tone="accent"
							label="open A/R"
							[value]="summary.balance | currency"
							[sub]="summary.customers + ' customers in this view'">
						</app-kpi-tile>
						<app-kpi-tile
							[tone]="summary.overdue > 0 ? 'failed' : 'settled'"
							label="past due"
							[value]="summary.overdue | currency"
							[sub]="(summary.b_current | currency) + ' still current'">
						</app-kpi-tile>
						<app-kpi-tile
							[tone]="summary.b_90plus > 0 ? 'failed' : 'default'"
							label="90+ days"
							[value]="summary.b_90plus | currency"
							[sub]="(summary.b_61_90 | currency) + ' in 61-90'">
						</app-kpi-tile>
						<app-kpi-tile
							*ngIf="installed"
							[tone]="summary.followups_due > 0 ? 'partial' : 'default'"
							label="follow-ups due"
							[value]="summary.followups_due"
							[sub]="summary.broken_promises + ' broken promise(s)'">
						</app-kpi-tile>
						<app-kpi-tile
							*ngIf="installed"
							[tone]="summary.promised_amount > 0 ? 'partial' : 'default'"
							label="promised"
							[value]="summary.promised_amount | currency"
							[sub]="summary.never_contacted + ' never contacted'">
						</app-kpi-tile>
					</div>

					<!-- Worklist -->
					<div class="d-flex justify-content-between align-items-center mt-4">
						<small class="text-muted">{{ rows.length }} customer<span *ngIf="rows.length !== 1">s</span></small>
						<div class="donotprint">
							<button mat-icon-button (click)="exportXls()" [disabled]="exporting || !hasExportableRows"
								matTooltip="Export to Excel" aria-label="Export to Excel">
								<i class="material-icons" aria-hidden="true">grid_on</i>
							</button>
							<button mat-icon-button (click)="exportPdf()" [disabled]="exporting || !hasExportableRows"
								matTooltip="Export to PDF" aria-label="Export to PDF">
								<i class="material-icons" aria-hidden="true">picture_as_pdf</i>
							</button>
						</div>
					</div>

					<div #printTable>
					<table class="feature-table feature-table--responsive">
						<thead>
							<tr>
								<th><button class="sort-head" (click)="sortBy('name')">Customer <i class="material-icons" aria-hidden="true">{{ sortIcon('name') }}</i></button></th>
								<th><button class="sort-head" (click)="sortBy('salesmanname')">Rep <i class="material-icons" aria-hidden="true">{{ sortIcon('salesmanname') }}</i></button></th>
								<th><button class="sort-head" (click)="sortBy('paymentterms')">Terms <i class="material-icons" aria-hidden="true">{{ sortIcon('paymentterms') }}</i></button></th>
								<th class="is-num"><button class="sort-head" (click)="sortBy('balance')">Open <i class="material-icons" aria-hidden="true">{{ sortIcon('balance') }}</i></button></th>
								<th class="is-num"><button class="sort-head" (click)="sortBy('b_1_30')">1-30 <i class="material-icons" aria-hidden="true">{{ sortIcon('b_1_30') }}</i></button></th>
								<th class="is-num"><button class="sort-head" (click)="sortBy('b_31_60')">31-60 <i class="material-icons" aria-hidden="true">{{ sortIcon('b_31_60') }}</i></button></th>
								<th class="is-num"><button class="sort-head" (click)="sortBy('b_61_90')">61-90 <i class="material-icons" aria-hidden="true">{{ sortIcon('b_61_90') }}</i></button></th>
								<th class="is-num"><button class="sort-head" (click)="sortBy('b_90plus')">90+ <i class="material-icons" aria-hidden="true">{{ sortIcon('b_90plus') }}</i></button></th>
								<th><button class="sort-head" (click)="sortBy('max_days')">Oldest <i class="material-icons" aria-hidden="true">{{ sortIcon('max_days') }}</i></button></th>
								<th *ngIf="installed"><button class="sort-head" (click)="sortBy('last_contact_date')">Last contact <i class="material-icons" aria-hidden="true">{{ sortIcon('last_contact_date') }}</i></button></th>
								<th *ngIf="installed">Next action</th>
								<th class="donotprint"></th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let r of sortedRows; trackBy: trackRow">
								<td data-label="Customer">
									<span>
										<strong>{{ r.name }}</strong>
										<div class="text-muted">
											{{ r.debtorno }}
											<a *ngIf="r.phoneno" [href]="telHref(r.phoneno)" matTooltip="Call this number">{{ r.phoneno }}</a>
											<a *ngIf="r.email" [href]="'mailto:' + r.email" matTooltip="Email — opens a draft">
												<i class="material-icons" aria-hidden="true" style="font-size: 14px; vertical-align: -2px;">email</i>
											</a>
										</div>
									</span>
								</td>
								<td data-label="Rep">{{ r.salesmanname || r.salesperson }}</td>
								<td data-label="Terms">{{ r.paymentterms }}</td>
								<td data-label="Open" class="is-num">{{ r.balance | currency }}</td>
								<td data-label="1-30" class="is-num" [class.is-zero]="!r.b_1_30">{{ r.b_1_30 | currency }}</td>
								<td data-label="31-60" class="is-num" [class.is-zero]="!r.b_31_60">{{ r.b_31_60 | currency }}</td>
								<td data-label="61-90" class="is-num" [class.is-zero]="!r.b_61_90">{{ r.b_61_90 | currency }}</td>
								<td data-label="90+" class="is-num" [class.is-zero]="!r.b_90plus">{{ r.b_90plus | currency }}</td>
								<td data-label="Oldest">
									<app-status-pill
										[status]="ageTone(r)"
										[label]="r.max_days > 0 ? (r.max_days + 'd') : 'current'">
									</app-status-pill>
								</td>
								<td data-label="Last contact" *ngIf="installed">
									<span *ngIf="r.last_contact_date">
										<span [matTooltip]="r.last_note || ''">{{ r.last_contact_date | date:'MMM d' }}</span>
										<app-status-pill
											[status]="lastStatusPill(r)"
											[label]="lastStatusLabel(r)">
										</app-status-pill>
										<div class="text-muted">
											{{ outcomeLabel(r.last_outcome) }} · {{ r.last_userid }}
											<span *ngIf="touchedToday(r)"> · {{ touchedAgo(r) }}</span>
										</div>
									</span>
									<span *ngIf="!r.last_contact_date" class="text-muted">never</span>
								</td>
								<td data-label="Next action" *ngIf="installed">
									<app-status-pill [status]="actionTone(r)" [label]="nextAction(r)"></app-status-pill>
								</td>
								<td class="donotprint">
									<button mat-icon-button (click)="open(r)"
										[attr.aria-label]="'Collections for ' + r.name"
										matTooltip="Open collections">
										<i class="material-icons" aria-hidden="true">call</i>
									</button>
								</td>
							</tr>
							<tr *ngIf="!rows.length">
								<td [attr.colspan]="installed ? 12 : 10" class="text-center">
									<span class="feature-empty is-settled">
										<i class="material-icons" aria-hidden="true">check_circle</i>
										Nothing open in this view
									</span>
								</td>
							</tr>
						</tbody>
					</table>
					</div>
				</div>

			</mat-card-content>
		</mat-card>
	</div>
</div>

<!-- Collections flyout — plain component overlay, no MatBottomSheet -->
<app-collection-sheet
	*ngIf="sheet"
	[data]="sheet"
	(closed)="onSheetClosed($event)">
</app-collection-sheet>
